<template>
  <div class="bf-filters">
    <div class="bf-period">
      <span
        v-for="period in periods"
        :key="period.value"
        class="bf-period__pill"
        :class="{ 'bf-period__pill--active': activePeriod === period.value }"
        @click="selectPeriod(period.value)"
      >
        {{ period.label }}
      </span>
    </div>

    <form
      v-if="activePeriod === 'custom'"
      class="bf-custom-range"
      @submit.prevent="handleFilter()"
    >
      <div class="bf-custom-range__field">
        <TextInput
          :type="'datetime-local'"
          :label="'Data Inicial'"
          v-model="initial_datetime"
          :required="false"
          @update:value="(initial_datetime) => updateInitDate(initial_datetime)"
        />
      </div>

      <div class="bf-custom-range__field">
        <TextInput
          :type="'datetime-local'"
          :label="'Data Final'"
          :required="false"
          v-model="final_datetime"
          @update:value="(final_datetime) => updateFinishDate(final_datetime)"
        />
      </div>

      <button type="submit" class="bf-btn bf-custom-range__apply">Aplicar</button>
    </form>
  </div>
</template>

<script lang="ts">
import { defineComponent, Ref, ref, watch } from "vue";

import { useAuthStore } from "@/stores/auth.store";
import { useDashboardStore } from "@/stores/dashboard.store";

import TextInput from "@/components/Forms/BasicElements/Input/TextInput.vue";

export default defineComponent({
  name: "DashboardFilters",
  props: {
    lastUpdated: {
      type: String,
      required: false,
    },
  },
  components: {
    TextInput,
  },
  setup(props) {
    const authStore = useAuthStore();
    const dashboardStore = useDashboardStore();

    const user: Ref = ref(authStore.getUser);

    const initial_datetime = ref(dashboardStore.filters.initial_datetime);
    const final_datetime = ref(dashboardStore.filters.final_datetime);

    const periods = [
      { value: "24h", label: "24h" },
      { value: "7d", label: "7 dias" },
      { value: "30d", label: "30 dias" },
      { value: "custom", label: "Personalizado" },
    ];
    const activePeriod = ref("custom");

    const toLocalInput = (date: Date): string => {
      const pad = (n: number) => String(n).padStart(2, "0");
      return (
        `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
        `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
      );
    };

    const formatDateTime = (value: string): string => {
      if (value.includes("T")) {
        const [datePart, timePart] = value.split("T");

        const [hours, minutes, seconds = "00"] = timePart.split(":");

        return `${datePart} ${hours}:${minutes}:${seconds}`;
      }

      if (value.includes(" ")) {
        const [datePart, timePart] = value.split(" ");

        const [hours, minutes, seconds = "00"] = timePart.split(":");

        return `${datePart} ${hours}:${minutes}:${seconds}`;
      }

      throw new Error("Formato de data inválido");
    };

    const updateInitDate = (value: string) => (initial_datetime.value = value);

    const updateFinishDate = (value: string) => (final_datetime.value = value);

    const handleFilter = () => {
      dashboardStore.setFilters({
        initial_datetime: initial_datetime.value
          ? formatDateTime(initial_datetime.value)
          : undefined,
        final_datetime: final_datetime.value
          ? formatDateTime(final_datetime.value)
          : undefined,
      });
    };

    const selectPeriod = (period: string) => {
      activePeriod.value = period;

      if (period === "custom") return;

      const now = new Date();
      const start = new Date(now);

      if (period === "24h") start.setDate(now.getDate() - 1);
      if (period === "7d") start.setDate(now.getDate() - 7);
      if (period === "30d") start.setDate(now.getDate() - 30);

      initial_datetime.value = toLocalInput(start);
      final_datetime.value = toLocalInput(now);

      handleFilter();
    };

    watch(
      () => dashboardStore.filters,
      (newFilters) => {
        (initial_datetime.value = newFilters.initial_datetime),
          (final_datetime.value = newFilters.final_datetime);
      }
    );

    return {
      props,
      user,
      initial_datetime,
      final_datetime,
      handleFilter,
      updateInitDate,
      updateFinishDate,
      periods,
      activePeriod,
      selectPeriod,
    };
  },
});
</script>

<style lang="scss" scoped>
.bf-filters {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.bf-period {
  display: flex;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  padding: 3px;
  gap: 2px;
  font-size: 12.5px;
  font-weight: 600;

  &__pill {
    padding: 7px 13px;
    border-radius: 8px;
    cursor: pointer;
    color: var(--text2);
    transition: background 0.15s ease, color 0.15s ease;

    &:hover {
      background: var(--hover);
    }

    &--active {
      background: var(--text);
      color: var(--bg);
      font-weight: 700;

      &:hover {
        background: var(--text);
      }
    }
  }
}

.bf-custom-range {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;

  &__field {
    min-width: 180px;

    :deep(.pingo-form-group) {
      margin-bottom: 0;
    }
  }

  &__apply {
    width: auto;
    white-space: nowrap;
  }
}
</style>
